import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import type { Content } from '@messenger/types'

import {
  editorPagesAtom,
  isBulkProcessingAtom,
  isEditorReadyAtom,
  isSavingAtom,
} from '@/atoms/_editorAtoms'
import {
  editorOrderPageSetAtom,
  editorProductIdAtom,
} from '@/atoms/editorAtoms'
import { Button } from '@/components/priority/Button/Button'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'
import { useEditorPageSetTurnedOver } from '@/hooks/editor/useEditorPageSetTurnedOver'
import { useEditorLotusOffered } from '@/hooks/editor/useEditorLotusOffered'
import {
  editorPageSetTurnoverCount,
  editorTextBoxListText,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { EditorTextBoxTurnoverModal } from './EditorTextBoxTurnoverModal'

const pageList = (pageNumbers: readonly number[], language?: string) =>
  editorTextBoxListText(pageNumbers.map(String), language)

/**
 * The Settings menu's action that turns the open page set over to Lotus
 * text boxes, for internal staff only and only where Lotus text boxes are
 * offered for the open order's funeral home (`useEditorLotusOffered`, as the
 * convert icon and the new text boxes switch). It says whether the set is turned
 * over, how many TinyMCE boxes on which pages a turnover would convert, and
 * how many it would leave as they are and where, as the pages stood at the
 * last sync of the canvas. Its action opens the confirmation, and is disabled
 * while a Save or a bulk action runs, before the editor is ready, and with
 * no order line.
 */
export const EditorTextBoxTurnoverControl = () => {
  const { t, i18n } = useTranslation()
  const offered = useEditorLotusOffered()
  const turnedOver = useEditorPageSetTurnedOver()
  const pages = useRecoilValue(editorPagesAtom) as unknown as Content[]
  const isSaving = useRecoilValue(isSavingAtom)
  const isBulkProcessing = useRecoilValue(isBulkProcessingAtom)
  const isEditorReady = useRecoilValue(isEditorReadyAtom)
  const lineId = useRecoilValue(editorProductIdAtom) as string | null
  const orderPageSet = useRecoilValue(editorOrderPageSetAtom) as {
    id?: string
  } | null
  const [confirming, setConfirming] = useState(false)
  const count = useMemo(
    () => (offered ? editorPageSetTurnoverCount(pages) : null),
    [offered, pages],
  )

  // The confirmation holds the running turnover's hook, which releases the
  // overlay and raises the result toast once the reloaded editor is ready. It
  // stays mounted when the control hides, because the reload clears the open
  // order for a moment and the control hides with it.
  const confirmation = (
    <EditorTextBoxTurnoverModal
      show={confirming}
      onClose={() => setConfirming(false)}
    />
  )

  // One fragment in both cases, so the confirmation keeps its place in the
  // tree, and its state, when the control hides.
  if (!offered || count === null)
    return (
      <>
        {null}
        {confirmation}
      </>
    )

  const disabled =
    isSaving ||
    isBulkProcessing ||
    !isEditorReady ||
    !lineId ||
    !orderPageSet?.id
  const language = i18n.resolvedLanguage

  return (
    <>
      <Flex
        column
        gap="8px"
        padding="0px 24px"
        margin="0 0 24px 0"
        data-testid="editor-text-box-turnover-control"
      >
        <Text size="14px" weight="600">
          {t('editor.textBox.turnover.control.title')}
        </Text>
        <Text
          size="12px"
          data-testid="editor-text-box-turnover-state"
          data-turned-over={turnedOver ? 'true' : 'false'}
        >
          {turnedOver
            ? t('editor.textBox.turnover.control.turnedOver')
            : t('editor.textBox.turnover.control.notTurnedOver')}
        </Text>
        <Text
          size="12px"
          data-testid="editor-text-box-turnover-converting"
          data-count={count.converting.count}
        >
          {count.converting.count === 0
            ? t('editor.textBox.turnover.control.convertingNone')
            : t('editor.textBox.turnover.control.converting', {
                count: count.converting.count,
                pages: pageList(count.converting.pageNumbers, language),
              })}
        </Text>
        {count.leaving.count > 0 && (
          <Text
            size="12px"
            data-testid="editor-text-box-turnover-leaving"
            data-count={count.leaving.count}
          >
            {t('editor.textBox.turnover.control.leaving', {
              count: count.leaving.count,
              pages: pageList(count.leaving.pageNumbers, language),
            })}
          </Text>
        )}
        <Button
          primary
          disabled={disabled}
          onClick={() => setConfirming(true)}
          data-testid="editor-text-box-turnover-action"
        >
          {t('editor.textBox.turnover.control.action')}
        </Button>
      </Flex>
      {confirmation}
    </>
  )
}
